import React, { useState, useEffect } from 'react';
import type { GuesshueState } from '../shared/game';
import { motion, AnimatePresence } from 'framer-motion';
import { ColorGrid } from './ColorGrid';
import { ClueInput } from './ClueInput';
import confetti from 'canvas-confetti';
import huesPalette from '../shared/hues_palette.json';

interface BoardProps {
  G: GuesshueState;
  ctx: any;
  moves: any;
  playerID?: string | null;
  matchData?: Array<{
    id: number;
    name?: string;
    isConnected?: boolean;
  }>;
}

export function GuesshueBoard({ G, ctx, moves, playerID, matchData }: BoardProps) {
  // Helper function to get actual color from palette
  const getActualTargetColor = () => {
    if (G.targetX !== undefined && G.targetY !== undefined && 
        G.targetY < huesPalette.length && G.targetX < huesPalette[G.targetY].length) {
      return huesPalette[G.targetY][G.targetX];
    }
    return G.targetColor || '#000';
  };
  
  // Helper function to get actual next color from palette
  const getActualNextTargetColor = () => {
    if (G.nextTargetX !== undefined && G.nextTargetY !== undefined && 
        G.nextTargetY < huesPalette.length && G.nextTargetX < huesPalette[G.nextTargetY].length) {
      return huesPalette[G.nextTargetY][G.nextTargetX];
    }
    return G.nextTargetColor || '#000';
  };

  // Wait for matchData to be populated
  const [isMatchDataReady, setIsMatchDataReady] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  
  React.useEffect(() => {
    if (matchData && matchData.length > 0) {
      console.log('matchData is ready:', matchData);
      setIsMatchDataReady(true);
    } else {
      console.log('matchData not ready yet, attempt:', connectionAttempts);
      setConnectionAttempts(prev => prev + 1);
    }
  }, [matchData, connectionAttempts]);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const isClueGiver = playerID === G.clueGiver;
  const isNextClueGiver = playerID === G.nextClueGiver;
  const currentPlayerNumber = playerID !== undefined && playerID !== null ? parseInt(playerID) + 1 : 0;
  
  // Helper function to get player's team
  const getPlayerTeam = (pid: string): string | null => {
    if (!G.teams) return null;
    if (G.teams['0'].includes(pid)) return '0';
    if (G.teams['1'].includes(pid)) return '1';
    return null;
  };
  
  // Determine if current player is on the active team
  const currentPlayerTeam = playerID && playerID !== null ? getPlayerTeam(playerID) : null;
  const clueGiverTeam = G.clueGiver ? getPlayerTeam(G.clueGiver) : null;
  const isOnActiveTeam = !G.gameMode || G.gameMode === 'cooperative' || G.gameMode === 'speed' || currentPlayerTeam === clueGiverTeam;
  const canMakeGuess = G.phase === 'guessing' && !isClueGiver && (isOnActiveTeam || G.gameMode === 'speed');
  
  // Game over state - but allow playing again
  const isGameOver = ctx.gameover !== undefined && G.phase === 'revealing';
  const winner = ctx.gameover?.winner;
  
  // Determine if current player's team won (for confetti)
  const didWin = React.useMemo(() => {
    if (!isGameOver || !winner) return false;
    
    if (G.gameMode === 'teams') {
      // Team mode
      if (winner === 'Team A' && currentPlayerTeam === '0') return true;
      if (winner === 'Team B' && currentPlayerTeam === '1') return true;
      return false;
    } else {
      // Cooperative mode - check if current player is the winner
      return playerID === winner;
    }
  }, [isGameOver, winner, G.gameMode, currentPlayerTeam, playerID]);
  
  // Trigger confetti for winners
  useEffect(() => {
    if (isGameOver && didWin) {
      // Fire confetti from multiple angles
      const duration = 5 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

      function randomInRange(min: number, max: number) {
        return Math.random() * (max - min) + min;
      }

      const interval: any = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        
        // Fire from left and right
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        });
      }, 250);
      
      return () => clearInterval(interval);
    }
  }, [isGameOver, didWin]);
  
  // Debug logging
  console.log('Board render:', { 
    playerID, 
    typeOfPlayerID: typeof playerID,
    playerIDUndefined: playerID === undefined,
    playerIDNull: playerID === null,
    matchData, 
    G, 
    ctx,
    isConnected: ctx.isConnected,
    gameover: ctx.gameover,
    isActive: ctx.isActive
  });
  
  // Additional debug to check matchData updates
  React.useEffect(() => {
    console.log('matchData updated:', matchData);
  }, [matchData]);
  
  // Get player names from matchData
  const playerNames = matchData?.map(player => player.name || `Player ${player.id + 1}`) || [];
  const connectedPlayers = matchData?.filter(p => p.isConnected).length || 0;
  
  // Check if game is ready to start
  // We need to check that matchData exists and has all required players connected with names
  const playersWithNames = matchData?.filter(p => p.name && p.isConnected) || [];
  const gameReady = G.gameMode === 'speed' 
    ? playersWithNames.length >= 2 // Speed mode can start with 2+ players
    : playersWithNames.length === ctx.numPlayers; // Other modes need exact player count
  const bothPlayersConnected = gameReady;
  
  // Debug logging for team games
  if (G.gameMode === 'teams') {
    console.log('Team game ready check:', {
      playersWithNames: playersWithNames.length,
      numPlayers: ctx.numPlayers,
      gameReady,
      teams: G.teams,
      matchData: matchData?.map(p => ({ id: p.id, name: p.name, isConnected: p.isConnected })),
      playerID,
      isPlayer0: playerID === '0',
      phase: G.phase
    });
  }
  
  // For development, show connection status
  React.useEffect(() => {
    console.log('Game state:', { 
      matchData, 
      playOrder: ctx.playOrder, 
      numPlayers: ctx.numPlayers,
      isConnected: ctx.isConnected,
      phase: ctx.phase,
      turn: ctx.turn,
      currentPlayer: ctx.currentPlayer,
      activePlayers: ctx.activePlayers
    });
    // Log when any player data changes
    if (matchData) {
      matchData.forEach((player, idx) => {
        console.log(`Player ${idx}:`, player);
      });
    }
  }, [matchData, ctx]);
  
  const handleColorSelect = (color: string, x: number, y: number) => {
    // Prevent any interaction if game is over
    if (isGameOver) {
      console.log('Game is over, ignoring color selection');
      return;
    }
    
    console.log('handleColorSelect called:', { 
      color, x, y, 
      phase: G.phase, 
      isClueGiver, 
      playerID,
      clueGiver: G.clueGiver,
      isGuessingPhase: G.phase === 'guessing',
      isNotClueGiver: !isClueGiver,
      movesObject: moves,
      hasMakeGuessFunction: typeof moves.makeGuess === 'function'
    });
    if (G.phase === 'giving-clue' && isClueGiver && !G.targetColor) {
      setSelectedColor(color);
      moves.selectTargetColor(color, x, y);
    } else if (canMakeGuess || (G.phase === 'stealing' && playerID && G.teams && G.stealingTeam && 
               getPlayerTeam(playerID) === G.stealingTeam && 
               (!G.stealGuesses![playerID] || G.stealGuesses![playerID].length === 0))) {
      console.log('About to call makeGuess...');
      console.log('moves object:', moves);
      console.log('moves.makeGuess type:', typeof moves.makeGuess);
      
      try {
        console.log('Calling makeGuess move with:', { x, y, color, playerID });
        console.log('G.guesses before move:', JSON.stringify(G.guesses));
        const result = moves.makeGuess(x, y, color);
        console.log('makeGuess returned:', result);
      } catch (error) {
        console.error('Error calling makeGuess:', error);
      }
      
      // Wait a bit and check state
      setTimeout(() => {
        console.log('Checking state after delay...');
        console.log('Current guesses state:', JSON.stringify(G.guesses));
        console.log('My guesses:', G.guesses[playerID || '']);
        console.log('Full game state:', JSON.stringify(G));
      }, 500);
    }
  };

  const handleClueSubmit = (clue: string) => {
    moves.giveClue(clue);
  };

  // Show loading state if matchData is not ready
  if (!isMatchDataReady && ctx.numPlayers > 1) {
    return (
      <div className="p-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Guess Hue</h1>
          <p className="text-gray-600">Loading player data...</p>
          {import.meta.env.DEV && (
            <div className="mt-4 text-xs text-gray-500">
              <p>Waiting for matchData to sync from server...</p>
              <p>Current matchData: {JSON.stringify(matchData)}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* Game Over Modal */}
      <AnimatePresence>
        {isGameOver && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 20 }}
              className={`bg-white rounded-lg shadow-2xl p-6 md:p-8 max-w-md w-full text-center ${
                didWin ? 'ring-4 ring-orange-500' : ''
              }`}
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
                winner === 'Tie' ? 'text-purple-600' : (didWin ? 'text-orange-600' : 'text-gray-800')
              }`}>
                {winner === 'Tie' ? '🤝 It\'s a Tie! 🤝' : (didWin ? '🎉 Victory! 🎉' : '😔 Defeat 😔')}
              </h2>
              
              {G.gameMode === 'teams' ? (
                <>
                  <p className="text-xl md:text-2xl mb-6 text-gray-700">
                    {winner === 'Tie' ? 'It\'s a tie!' : `${winner} wins!`}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className={`rounded-lg p-4 ${
                      winner === 'Tie' ? 'bg-purple-100 ring-2 ring-purple-500' :
                      winner === 'Team A' ? 'bg-orange-100 ring-2 ring-orange-500' : 'bg-gray-100'
                    }`}>
                      <h3 className="font-semibold text-lg mb-2">Team A</h3>
                      <p className="text-3xl font-bold text-gray-900">{G.teamScores?.['0'] || 0}</p>
                      <div className="mt-2 text-sm text-gray-600">
                        {G.teams?.['0'].map(pid => {
                          const pIndex = parseInt(pid);
                          const pName = matchData?.[pIndex]?.name || `Player ${pIndex + 1}`;
                          return (
                            <p key={pid}>
                              {pName}: {G.scores[pid]} pts
                            </p>
                          );
                        })}
                      </div>
                    </div>
                    
                    <div className={`rounded-lg p-4 ${
                      winner === 'Tie' ? 'bg-purple-100 ring-2 ring-purple-500' :
                      winner === 'Team B' ? 'bg-lightning-100 ring-2 ring-lightning-500' : 'bg-gray-100'
                    }`}>
                      <h3 className="font-semibold text-lg mb-2">Team B</h3>
                      <p className="text-3xl font-bold text-gray-900">{G.teamScores?.['1'] || 0}</p>
                      <div className="mt-2 text-sm text-gray-600">
                        {G.teams?.['1'].map(pid => {
                          const pIndex = parseInt(pid);
                          const pName = matchData?.[pIndex]?.name || `Player ${pIndex + 1}`;
                          return (
                            <p key={pid}>
                              {pName}: {G.scores[pid]} pts
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xl md:text-2xl mb-6 text-gray-700">
                    {matchData?.[parseInt(winner)]?.name || `Player ${parseInt(winner) + 1}`} wins!
                  </p>
                  
                  <div className="space-y-2 mb-6">
                    {Object.entries(G.scores)
                      .sort(([, a], [, b]) => b - a)
                      .map(([pid, score]) => {
                        const playerIndex = parseInt(pid);
                        const playerName = matchData?.[playerIndex]?.name || `Player ${playerIndex + 1}`;
                        const isWinner = pid === winner;
                        
                        return (
                          <div 
                            key={pid} 
                            className={`rounded p-3 ${
                              isWinner ? 'bg-orange-100 ring-2 ring-orange-500' : 'bg-gray-100'
                            }`}
                          >
                            <p className="font-medium">
                              {playerName} {pid === playerID && '(You)'}
                            </p>
                            <p className="text-2xl font-bold">{score} points</p>
                          </div>
                        );
                      })}
                  </div>
                </>
              )}
              
              <button
                onClick={() => moves.playAgain()}
                className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-semibold"
              >
                Play Again
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        <header className="text-center mb-4 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2">Guess Hue</h1>
          <p className="text-sm md:text-base text-gray-600">
            {G.gameMode === 'speed' && '⚡ Speed Mode • '}
            Round {G.round + 1} • 
            {G.phase === 'waiting' && ' Waiting for players'}
            {G.phase === 'giving-clue' && ' Selecting color & giving clue'}
            {G.phase === 'guessing' && G.gameMode === 'speed' ? ' Race to guess!' : ' Time to guess!'}
            {G.phase === 'stealing' && ' Steal opportunity!'}
            {G.phase === 'revealing' && ' Results'}
          </p>
          {G.clueGiver && matchData && (
            <p className="text-sm text-gray-500 mt-1">
              {isClueGiver 
                ? "You are the clue giver!" 
                : `${matchData[parseInt(G.clueGiver)]?.name || `Player ${parseInt(G.clueGiver) + 1}`} is giving clues`}
            </p>
          )}
        </header>

        <div className="bg-white rounded-lg shadow-lg p-4 md:p-6">
          {/* Connection status for debugging - only show in development */}
          {import.meta.env.DEV && (
            <div className="mb-4 text-xs text-gray-500 text-center hidden md:block">
              <div>Connected: {connectedPlayers}/{ctx.numPlayers} | Named Players: {playersWithNames.length}/{ctx.numPlayers} | You are Player {currentPlayerNumber} | Game Ready: {gameReady ? 'Yes' : 'No'}</div>
              <div>MatchData: {JSON.stringify(matchData?.map(p => ({ name: p.name, connected: p.isConnected })))}</div>
            </div>
          )}
          
          {G.phase === 'waiting' && (
            <div className="text-center">
              {!gameReady ? (
                <>
                  {G.gameMode === 'speed' ? (
                    <p className="text-xl text-gray-700 mb-4">
                      Waiting for players... ({playersWithNames.length}/{ctx.numPlayers} joined, need 2+ to start)
                    </p>
                  ) : (
                    <p className="text-xl text-gray-700 mb-4">
                      Waiting for players to join... ({playersWithNames.length}/{ctx.numPlayers})
                    </p>
                  )}
                </>
              ) : (
                <>
                  {G.gameMode === 'speed' ? (
                    <p className="text-xl text-gray-700 mb-4">
                      {playersWithNames.length} players ready! Host can start anytime.
                    </p>
                  ) : (
                    <p className="text-xl text-gray-700 mb-4">
                      All {ctx.numPlayers} players connected! Ready to start.
                    </p>
                  )}
                </>
              )}
              
              {/* Show team selection for team mode */}
              {G.gameMode === 'teams' && matchData && (
                <div className="mt-4 space-y-4">
                  {/* Team selection for current player */}
                  {playerID && G.teamSelections && G.teamSelections[playerID] === null && (
                    <div className="mb-6 p-4 bg-orange-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-700 mb-3">Choose your team:</p>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => moves.selectTeam('0')}
                          className="px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                          disabled={G.teams && G.teams['0'].length >= 2}
                        >
                          Join Team A
                          {G.teams && ` (${G.teams['0'].length}/2)`}
                        </button>
                        <button
                          onClick={() => moves.selectTeam('1')}
                          className="px-4 py-3 bg-lightning-500 text-white rounded-lg hover:bg-lightning-600 transition-colors"
                          disabled={G.teams && G.teams['1'].length >= 2}
                        >
                          Join Team B
                          {G.teams && ` (${G.teams['1'].length}/2)`}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Team display */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-orange-50 rounded-lg p-3">
                      <div className="text-sm font-medium text-orange-800 mb-2">Team A</div>
                      {G.teams && G.teams['0'].map(pid => {
                        const idx = parseInt(pid);
                        const player = matchData[idx];
                        return (
                          <p key={pid} className="text-sm text-orange-700">
                            {player?.name || `Player ${idx + 1}`} {pid === playerID && '(You)'}
                          </p>
                        );
                      })}
                      {G.teams && G.teams['0'].length === 0 && (
                        <p className="text-sm text-gray-400">No players yet</p>
                      )}
                    </div>
                    <div className="bg-lightning-50 rounded-lg p-3">
                      <div className="text-sm font-medium text-lightning-800 mb-2">Team B</div>
                      {G.teams && G.teams['1'].map(pid => {
                        const idx = parseInt(pid);
                        const player = matchData[idx];
                        return (
                          <p key={pid} className="text-sm text-lightning-700">
                            {player?.name || `Player ${idx + 1}`} {pid === playerID && '(You)'}
                          </p>
                        );
                      })}
                      {G.teams && G.teams['1'].length === 0 && (
                        <p className="text-sm text-gray-400">No players yet</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Waiting players */}
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Waiting to choose:</p>
                    {matchData.map((player, idx) => {
                      const pid = idx.toString();
                      if (!player?.isConnected) {
                        return (
                          <p key={idx} className="text-sm text-gray-400">
                            Player {idx + 1}: (not connected)
                          </p>
                        );
                      }
                      if (G.teamSelections && G.teamSelections[pid] === null) {
                        return (
                          <p key={idx} className="text-sm text-gray-600">
                            {player.name || `Player ${idx + 1}`} - choosing team...
                          </p>
                        );
                      }
                      return null;
                    })}
                  </div>
                  
                  {/* Team balance status */}
                  {G.teams && (
                    <div className="mt-4">
                      {G.teams['0'].length === 2 && G.teams['1'].length === 2 ? (
                        <p className="text-sm text-green-600">✓ Teams are balanced and ready!</p>
                      ) : (
                        <p className="text-sm text-orange-600">⚠️ Waiting for balanced teams (2v2)</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Show connected players for non-team games */}
              {G.gameMode !== 'teams' && matchData && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600">
                    Connected players: {playersWithNames.length}/{ctx.numPlayers}
                  </p>
                  {/* For speed mode with many players, show a compact view */}
                  {G.gameMode === 'speed' && ctx.numPlayers > 6 ? (
                    <div className="mt-2 flex flex-wrap gap-2 justify-center">
                      {Array.from({ length: ctx.numPlayers }, (_, idx) => {
                        const player = matchData[idx];
                        if (player?.isConnected && player.name) {
                          return (
                            <div key={idx} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                              {player.name}
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  ) : (
                    // Regular list for fewer players
                    Array.from({ length: ctx.numPlayers }, (_, idx) => {
                      const player = matchData[idx];
                      if (player) {
                        return (
                          <p key={idx} className={`text-sm ${player.isConnected ? 'text-green-600' : 'text-gray-400'}`}>
                            Player {idx + 1}: {player.name || '(empty slot)'} {player.isConnected ? '✓' : '- waiting to join'}
                          </p>
                        );
                      } else {
                        return (
                          <p key={idx} className="text-sm text-gray-400">
                            Player {idx + 1}: (empty slot)
                          </p>
                        );
                      }
                    })
                  )}
                </div>
              )}
              
              {/* Start game button - show when ready */}
              {gameReady && (
                <div className="mt-6">
                  {playerID === '0' ? (
                    <button
                      onClick={() => moves.startGame()}
                      className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                      disabled={G.gameMode === 'teams' && G.teams && (G.teams['0'].length !== 2 || G.teams['1'].length !== 2)}
                    >
                      Start Game
                    </button>
                  ) : (
                    <p className="text-gray-600">Waiting for {matchData?.[0]?.name || 'Player 1'} to start the game...</p>
                  )}
                </div>
              )}
            </div>
          )}

          {G.phase === 'giving-clue' && (
            <div className="space-y-6">
              {isClueGiver ? (
                <>
                  <div className="text-center">
                    <p className="text-xl text-gray-700 mb-4">
                      {!G.targetColor ? "Select a color from the grid below:" : 
                       G.autoSelectColor ? "Here's your randomly selected color. Give a clue:" : 
                       "Now give a clue for your color:"}
                    </p>
                  </div>
                  
                  {/* Always show the grid */}
                  <div className="space-y-2">
                    <div className="flex justify-center">
                      <ColorGrid
                        onColorSelect={!G.targetColor ? handleColorSelect : undefined}
                        isSelectable={!G.targetColor}
                        selectedColors={G.targetColor && G.targetX !== undefined && G.targetY !== undefined ? 
                          [{x: G.targetX, y: G.targetY, color: G.targetColor}] : []}
                      />
                    </div>
                    <p className="text-xs text-gray-500 text-center md:hidden">
                      Swipe to explore all colors
                    </p>
                  </div>
                  
                  {G.targetColor && (
                    <div className="space-y-4">
                      {G.autoSelectColor && (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ type: "spring", damping: 20 }}
                          className="mb-2"
                        >
                          <p className="text-sm text-orange-600 font-medium">
                            🎲 Color randomly selected!
                          </p>
                        </motion.div>
                      )}
                      <div className="flex justify-center">
                        <motion.div 
                          className="w-32 h-32 rounded-lg shadow-lg" 
                          style={{ backgroundColor: getActualTargetColor() }}
                          initial={G.autoSelectColor ? { scale: 0, rotate: 180 } : {}}
                          animate={G.autoSelectColor ? { scale: 1, rotate: 0 } : {}}
                          transition={{ type: "spring", damping: 15 }}
                        />
                      </div>
                      <ClueInput onSubmit={handleClueSubmit} />
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-6">
                  <div className="text-center">
                    <p className="text-xl text-gray-700">
                      Waiting for {matchData?.[parseInt(G.clueGiver || '0')]?.name || `Player ${parseInt(G.clueGiver || '0') + 1}`} to {G.autoSelectColor ? 'give a clue for their randomly selected color' : 'select a color and give a clue'}...
                    </p>
                  </div>
                  
                  {/* Show grid to spectators without revealing the selection */}
                  <div className="space-y-2">
                    <div className="flex justify-center">
                      <ColorGrid
                        onColorSelect={() => {}}
                        isSelectable={false}
                        selectedColors={[]} // Don't show the selection to other players!
                      />
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      {G.targetColor ? "The clue giver has selected a color" : "Watching the clue giver select a color"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {G.phase === 'guessing' && (
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-xl text-gray-700 mb-2">
                  Clue: <span className="font-bold text-2xl text-orange-600">{G.currentClue}</span>
                </p>
                {isClueGiver ? (
                  <p className="text-gray-600">Wait for other players to guess...</p>
                ) : isOnActiveTeam || G.gameMode === 'speed' ? (
                  <p className="text-gray-600">
                    {G.gameMode === 'speed' 
                      ? "Be the first to click the correct color! ⚡" 
                      : "Click on the colors you think match the clue! (Up to 3 guesses)"}
                  </p>
                ) : (
                  <p className="text-gray-600">Spectating the other team's turn...</p>
                )}
              </div>
              
              {/* Show preparation area for next clue giver in team mode */}
              {G.gameMode === 'teams' && isNextClueGiver && !G.nextClue && G.autoSelectColor && G.nextTargetColor && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 bg-lightning-50 rounded-lg border-2 border-lightning-200"
                >
                  <p className="text-sm font-semibold text-lightning-800 mb-3">
                    🎯 Prepare for your turn! Here's your randomly selected color:
                  </p>
                  <div className="space-y-4">
                    <div className="flex justify-center">
                      <div className="text-center">
                        <div className="w-24 h-24 rounded-lg shadow-lg mb-3" style={{ 
                          backgroundColor: getActualNextTargetColor()
                        }} />
                        <p className="text-xs text-gray-600 mb-3">Think of a clue for this color</p>
                      </div>
                    </div>
                    <ClueInput 
                      onSubmit={(clue) => moves.giveNextClue(clue)} 
                      placeholder="Enter your clue..."
                    />
                  </div>
                </motion.div>
              )}
              
              {/* Show ready status for next clue giver */}
              {G.gameMode === 'teams' && isNextClueGiver && G.nextClue && (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200"
                >
                  <p className="text-sm font-medium text-green-800">
                    ✅ You're ready! Your clue is prepared for the next round.
                  </p>
                </motion.div>
              )}
              
              {/* Always show the grid during guessing phase */}
              <div className="space-y-4">
                <div className="flex justify-center">
                  <ColorGrid
                    onColorSelect={canMakeGuess ? handleColorSelect : undefined}
                    selectedColors={isClueGiver || !isOnActiveTeam ? 
                      Object.values(G.guesses).flat() : 
                      (G.guesses[playerID || ''] || [])}
                    isSelectable={canMakeGuess}
                    gameMode={G.gameMode}
                    currentPlayerId={playerID}
                  />
                </div>
                <div className="text-center">
                  {!isClueGiver && (isOnActiveTeam || G.gameMode === 'speed') && (
                    <>
                      {G.gameMode !== 'speed' && (
                        <p className="text-sm text-gray-600">
                          Guesses made: {(G.guesses[playerID || ''] || []).length} / 3
                        </p>
                      )}
                      {G.speedModeWinner && G.gameMode === 'speed' && (
                        <motion.p 
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="text-lg font-bold text-orange-600"
                        >
                          {G.speedModeWinner === playerID ? '🎉 You won!' : 
                           `${matchData?.[parseInt(G.speedModeWinner)]?.name || `Player ${parseInt(G.speedModeWinner) + 1}`} won!`}
                        </motion.p>
                      )}
                      <p className="text-xs text-gray-500 md:hidden mt-1">
                        Swipe to explore all colors
                      </p>
                    </>
                  )}
                  {isClueGiver && (
                    <>
                      <p className="text-sm text-gray-600">
                        Watching players make their guesses
                      </p>
                      {G.gameMode === 'speed' && !G.speedModeWinner && (
                        <button
                          onClick={() => moves.revealAnswer()}
                          className="mt-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                        >
                          Reveal Answer (No one guessed)
                        </button>
                      )}
                    </>
                  )}
                  {!isClueGiver && !isOnActiveTeam && (
                    <p className="text-sm text-gray-600">
                      Watch as the other team makes their guesses
                    </p>
                  )}
                  
                  {/* Player color legend for speed mode */}
                  {G.gameMode === 'speed' && (
                    <div className="mt-4 flex justify-center items-center gap-4 text-xs">
                      <p className="text-gray-500 mr-2">Player colors:</p>
                      {Object.keys(G.scores).filter(pid => pid !== G.clueGiver).map((pid) => {
                        const playerColors = ['orange', 'lightning', 'green', 'purple'];
                        const colorName = playerColors[parseInt(pid) % playerColors.length];
                        const colorClasses: Record<string, string> = {
                          'orange': 'bg-orange-500',
                          'lightning': 'bg-lightning-500',
                          'green': 'bg-green-500',
                          'purple': 'bg-purple-500'
                        };
                        return (
                          <div key={pid} className="flex items-center gap-1">
                            <div className={`w-3 h-3 rounded-full ${colorClasses[colorName]}`} />
                            <span className="text-gray-600">
                              {matchData?.[parseInt(pid)]?.name || `Player ${parseInt(pid) + 1}`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Debug info - only show in development */}
                  {import.meta.env.DEV && !isClueGiver && isOnActiveTeam && (
                    <div className="mt-2 text-xs text-gray-400">
                      <p>PlayerID: {playerID} | Type: {typeof playerID}</p>
                      <p>Guesses obj: {JSON.stringify(G.guesses)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {G.phase === 'stealing' && (
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-xl text-gray-700 mb-2">
                  Clue: <span className="font-bold text-2xl text-orange-600">{G.currentClue}</span>
                </p>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="mb-2"
                >
                  <p className="text-lg font-bold text-lightning-600">
                    🎯 Steal Opportunity!
                  </p>
                  <p className="text-sm text-gray-600">
                    {G.stealingTeam === '0' ? 'Team A' : 'Team B'} can steal 1 point!
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Each player gets one guess - exact match scores 1 point
                  </p>
                </motion.div>
              </div>
              
              {/* Show preparation area for next clue giver during steal phase */}
              {G.gameMode === 'teams' && isNextClueGiver && !G.nextClue && G.autoSelectColor && G.nextTargetColor && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 bg-lightning-50 rounded-lg border-2 border-lightning-200"
                >
                  <p className="text-sm font-semibold text-lightning-800 mb-3">
                    🎯 While they're stealing, prepare your clue!
                  </p>
                  <div className="space-y-4">
                    <div className="flex justify-center">
                      <div className="text-center">
                        <div className="w-24 h-24 rounded-lg shadow-lg mb-3" style={{ 
                          backgroundColor: getActualNextTargetColor()
                        }} />
                        <p className="text-xs text-gray-600 mb-3">Your color for next round</p>
                      </div>
                    </div>
                    <ClueInput 
                      onSubmit={(clue) => moves.giveNextClue(clue)} 
                      placeholder="Enter your clue..."
                    />
                  </div>
                </motion.div>
              )}
              
              {/* Show ready status */}
              {G.gameMode === 'teams' && isNextClueGiver && G.nextClue && (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200"
                >
                  <p className="text-sm font-medium text-green-800">
                    ✅ You're ready! Your clue is prepared for the next round.
                  </p>
                </motion.div>
              )}
              
              <div className="space-y-4">
                <div className="flex justify-center">
                  <ColorGrid
                    onColorSelect={
                      playerID && G.teams && G.stealingTeam && 
                      getPlayerTeam(playerID) === G.stealingTeam &&
                      (!G.stealGuesses![playerID] || G.stealGuesses![playerID].length === 0)
                        ? handleColorSelect 
                        : undefined
                    }
                    selectedColors={[
                      ...(G.stealGuesses ? Object.values(G.stealGuesses).flat() : []),
                      ...Object.values(G.guesses).flat()
                    ]}
                    isSelectable={
                      playerID !== null && G.teams && G.stealingTeam &&
                      getPlayerTeam(playerID) === G.stealingTeam &&
                      (!G.stealGuesses![playerID] || G.stealGuesses![playerID].length === 0)
                    }
                  />
                </div>
                <div className="text-center">
                  {playerID && G.teams && G.stealingTeam && getPlayerTeam(playerID) === G.stealingTeam ? (
                    <p className="text-sm text-gray-600">
                      {G.stealGuesses![playerID] && G.stealGuesses![playerID].length > 0
                        ? "You've made your steal attempt"
                        : "Click a color to attempt a steal"}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-600">
                      Watching the steal attempt...
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {G.phase === 'revealing' && (
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-xl text-gray-700 mb-2">
                  The clue was: <span className="font-bold text-2xl text-orange-600">{G.currentClue}</span>
                </p>
                {G.gameMode === 'speed' && (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="mb-4"
                  >
                    {G.speedModeWinner ? (
                      <>
                        <p className="text-2xl font-bold text-orange-600">
                          ⚡ {matchData?.[parseInt(G.speedModeWinner)]?.name || `Player ${parseInt(G.speedModeWinner) + 1}`} won this round!
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          +5 points for the winner, +2 points for the clue giver
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-xl font-bold text-gray-600">
                          No one guessed correctly this round!
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Better luck next time!
                        </p>
                      </>
                    )}
                  </motion.div>
                )}
                <div className="flex justify-center mt-4 mb-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Target Color:</p>
                    <div className="w-32 h-32 rounded-lg shadow-lg" style={{ backgroundColor: getActualTargetColor() }} />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center">
                <ColorGrid
                  selectedColors={[
                    ...Object.values(G.guesses).flat(),
                    ...(G.stealGuesses ? Object.values(G.stealGuesses).flat() : [])
                  ]}
                  isSelectable={false}
                  showTarget={true}
                  targetColor={G.targetColor}
                  targetX={G.targetX}
                  targetY={G.targetY}
                  gameMode={G.gameMode}
                  currentPlayerId={playerID}
                />
              </div>
              
              {G.gameMode === 'teams' && G.stealingTeam && G.stealGuesses && Object.keys(G.stealGuesses).length > 0 && (
                <motion.div 
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center mb-4"
                >
                  {G.stealSuccessful ? (
                    <div className="p-4 bg-green-100 rounded-lg">
                      <p className="text-lg font-bold text-green-800">
                        🎯 Steal Successful!
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        {G.stealingTeam === '0' ? 'Team A' : 'Team B'} scored 1 point!
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-100 rounded-lg">
                      <p className="text-sm font-medium text-gray-700">
                        {G.stealingTeam === '0' ? 'Team A' : 'Team B'} attempted to steal but missed
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
              
              <div className="text-center">
                <button
                  onClick={() => moves.nextRound()}
                  className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Next Round
                </button>
              </div>
            </div>
          )}

          {/* Scores */}
          {G.gameMode === 'teams' && G.teamScores ? (
            <div className="mt-8 space-y-4">
              <h3 className="text-center text-lg font-semibold text-gray-900">Team Scores</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-orange-100 rounded-lg p-4 text-center">
                  <p className="text-sm font-medium text-orange-800 mb-1">Team A</p>
                  <p className="text-3xl font-bold text-orange-900">{G.teamScores['0']}</p>
                  <div className="mt-2 space-y-1">
                    {G.teams?.['0'].map(pid => {
                      const pIndex = parseInt(pid);
                      const pName = matchData?.[pIndex]?.name || `Player ${pIndex + 1}`;
                      return (
                        <p key={pid} className="text-xs text-orange-700">
                          {pName} {pid === playerID && '(You)'}: {G.scores[pid]}
                        </p>
                      );
                    })}
                  </div>
                </div>
                <div className="bg-lightning-100 rounded-lg p-4 text-center">
                  <p className="text-sm font-medium text-lightning-800 mb-1">Team B</p>
                  <p className="text-3xl font-bold text-lightning-900">{G.teamScores['1']}</p>
                  <div className="mt-2 space-y-1">
                    {G.teams?.['1'].map(pid => {
                      const pIndex = parseInt(pid);
                      const pName = matchData?.[pIndex]?.name || `Player ${pIndex + 1}`;
                      return (
                        <p key={pid} className="text-xs text-lightning-700">
                          {pName} {pid === playerID && '(You)'}: {G.scores[pid]}
                        </p>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-2 gap-4">
              {Object.entries(G.scores).map(([pid, score]) => {
                const playerIndex = parseInt(pid);
                const playerName = matchData?.[playerIndex]?.name || `Player ${playerIndex + 1}`;
                const isCurrentPlayer = pid === playerID;
                
                return (
                  <div 
                    key={pid} 
                    className={`rounded p-3 text-center ${
                      isCurrentPlayer ? 'bg-orange-100 ring-2 ring-orange-500' : 'bg-gray-100'
                    }`}
                  >
                    <p className="text-sm text-gray-600">
                      {playerName} {isCurrentPlayer && '(You)'}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">{score}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}